import { useEffect, useMemo, useState } from "react";
import getDataFromApi from "../../../../../components/getDataFromApi";
import {
    UserPlusIcon,
    PlusCircleIcon,
    CloseIcon,
} from "../../../../Common/SvgIcons/WorkflowIcons";
import "./organizations.scss";
import { Button } from "reactstrap";

export default function CreateUser({ setCreate, onSuccess, tenantId }) {
    const [schema, setSchema] = useState(null);
    const [uiSchema, setUiSchema] = useState(null);

    const [formLoading, setFormLoading] = useState(true);
    const [form, setForm] = useState({});
    const [errors, setErrors] = useState({});
    const [fetchError, setFetchError] = useState(null);
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const normalizeKey = (key) => key.replace(/\s+/g, "_").toLowerCase();

    /* =========================
       FETCH SCHEMA
    ========================== */

    console.log('schema', schema)
    useEffect(() => {
        getDataFromApi(`${window.ENV.API_URL}/forms/admin/create_user`, "get")
            .then((result) => {
                setSchema(result.json_schema);
                setUiSchema(result.ui_schema || null);
                setFormLoading(false);
            })
            .catch((e) => {
                setFetchError(e);
                setFormLoading(false);
            });
    }, []);

    /* =========================
       INIT FORM
    ========================== */
    useEffect(() => {
        if (!schema?.properties) return;

        const f = {};
        const e = {};

        Object.entries(schema.properties).forEach(([label, field]) => {
            const key = normalizeKey(label);
            if (field.enum) f[key] = field.default || field.enum[0];
            else f[key] = ""; // ✅ do not prefill
            e[key] = false;
        });

        // ✅ extra UI-only field: confirm password
        f.confirm_password = "";
        e.confirm_password = false;

        setForm(f);
        setErrors(e);
    }, [schema]);

    const isRequired = (label) => schema?.required?.includes(label);

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const handleChange = (key, value, required) => {
        setForm((prev) => ({ ...prev, [key]: value }));

        if (required) {
            setErrors((prev) => ({
                ...prev,
                [key]: !value?.toString().trim(),
            }));
        } else {
            // clear error as user types for non-required extras (like confirm password)
            setErrors((prev) => ({ ...prev, [key]: false }));
        }
    };

    /* =========================
       PASSWORD: GENERATE + STRENGTH
    ========================== */
    const generateSecurePassword = (length = 16) => {
        // Use Web Crypto if available (secure)
        const lower = "abcdefghijklmnopqrstuvwxyz";
        const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const digits = "0123456789";
        const symbols = "!@#$%^&*()-_=+[]{}|;:,.<>?/";
        const all = lower + upper + digits + symbols;

        const getRand = (max) => {
            // crypto.getRandomValues is secure; fallback to Math.random only if needed
            if (window?.crypto?.getRandomValues) {
                const buf = new Uint32Array(1);
                window.crypto.getRandomValues(buf);
                return buf[0] % max;
            }
            return Math.floor(Math.random() * max);
        };

        // ensure at least 1 from each class
        const chars = [
            lower[getRand(lower.length)],
            upper[getRand(upper.length)],
            digits[getRand(digits.length)],
            symbols[getRand(symbols.length)],
        ];

        for (let i = chars.length; i < length; i++) {
            chars.push(all[getRand(all.length)]);
        }

        // shuffle
        for (let i = chars.length - 1; i > 0; i--) {
            const j = getRand(i + 1);
            [chars[i], chars[j]] = [chars[j], chars[i]];
        }

        return chars.join("");
    };

    const passwordValue = form.password || "";
    const confirmPasswordValue = form.confirm_password || "";

    const passwordChecks = useMemo(() => {
        const pwd = passwordValue;

        return {
            length: pwd.length >= 6,
            lower: /[a-z]/.test(pwd),
            upper: /[A-Z]/.test(pwd),
            number: /[0-9]/.test(pwd),
            symbol: /[^A-Za-z0-9]/.test(pwd),
            noSpaces: !/\s/.test(pwd),
        };
    }, [passwordValue]);

    const passwordScore = useMemo(() => {
        // simple scoring: 0-6
        const values = Object.values(passwordChecks);
        return values.reduce((acc, ok) => acc + (ok ? 1 : 0), 0);
    }, [passwordChecks]);

    const passwordStrengthLabel = useMemo(() => {
        if (!passwordValue) return "";

        // 🔴 Rule 1: less than 8 = always Weak
        if (passwordValue.length < 8) return "Weak";

        // After 8 chars, use score
        if (passwordScore <= 2) return "Weak";
        if (passwordScore <= 4) return "Medium";
        return "Strong";
    }, [passwordScore, passwordValue]);


    // const isPasswordStrongEnough = useMemo(() => {
    //     // Require minimum: length + noSpaces + any 3 of (lower/upper/number/symbol)
    //     const core = passwordChecks.length && passwordChecks.noSpaces;
    //     const varietyCount =
    //         (passwordChecks.lower ? 1 : 0) +
    //         (passwordChecks.upper ? 1 : 0) +
    //         (passwordChecks.number ? 1 : 0) +
    //         (passwordChecks.symbol ? 1 : 0);

    //     return !!passwordValue && core && varietyCount >= 3;
    // }, [passwordChecks, passwordValue]);

    const isPasswordStrongEnough = useMemo(() => {
        if (!passwordValue) return false;

        // must be at least 8
        if (passwordValue.length < 8) return false;

        return passwordStrengthLabel === "Medium" || passwordStrengthLabel === "Strong";
    }, [passwordStrengthLabel, passwordValue]);


    const passwordsMatch = useMemo(() => {
        if (!confirmPasswordValue) return true; // don’t show mismatch until user types
        return passwordValue === confirmPasswordValue;
    }, [passwordValue, confirmPasswordValue]);

    const confirmPasswordProgress = useMemo(() => {
        if (!confirmPasswordValue || !passwordValue) return 0;

        let matchCount = 0;
        const maxLength = Math.max(passwordValue.length, confirmPasswordValue.length);

        for (let i = 0; i < confirmPasswordValue.length; i++) {
            if (confirmPasswordValue[i] === passwordValue[i]) {
                matchCount++;
            } else {
                break;
            }
        }

        return maxLength ? Math.round((matchCount / passwordValue.length) * 100) : 0;
    }, [passwordValue, confirmPasswordValue]);

    const handleGeneratePassword = () => {
        const pwd = generateSecurePassword(16);
        setForm((prev) => ({
            ...prev,
            password: pwd,
            confirm_password: pwd,
        }));
        setErrors((prev) => ({
            ...prev,
            password: false,
            confirm_password: false,
        }));
    };

    /* =========================
       SUBMIT
    ========================== */
    const handleSubmit = async () => {
        if (!schema?.properties) return;

        setSubmitError(null);

        const newErrors = {};

        // validate schema fields
        Object.keys(schema.properties).forEach((label) => {
            const key = normalizeKey(label);
            const value = form[key]?.toString().trim();

            if (isRequired(label) && !value) {
                newErrors[key] = true;
                return;
            }

            if (label === "Email" && value && !isValidEmail(value)) {
                newErrors[key] = true;
            }
        });

        // validate confirm password (UI-only)
        if (!form.confirm_password?.toString().trim()) {
            newErrors.confirm_password = true;
        } else if (form.password !== form.confirm_password) {
            newErrors.confirm_password = true;
        }

        // validate password strength
        if (form.password && !isPasswordStrongEnough) {
            newErrors.password = true;
        }

        setErrors((prev) => ({ ...prev, ...newErrors }));
        if (Object.keys(newErrors).length > 0) return;

        // payload only includes schema fields
        const payload = {};
        Object.keys(schema.properties).forEach((label) => {
            payload[label] = form[normalizeKey(label)];
            payload['tenant_id'] = tenantId;
        });

        // console.log('user payload', payload)

        try {
            setSubmitting(true);
            await getDataFromApi(
                `${window.ENV.API_URL}/forms/admin/create_user`,
                "post",
                payload
            );
            onSuccess?.();
            setCreate(false);
        } catch (e) {
            let message = "Failed to create user. Please try again.";

            const detail =
                e?.response?.data?.detail ||
                e?.body?.detail ||
                e?.detail ||
                e?.response?.data?.message ||
                e?.message;

            if (detail) {
                try {
                    const raw = String(detail);
                    const jsonStart = raw.indexOf("{");

                    if (jsonStart !== -1) {
                        const parsed = JSON.parse(raw.substring(jsonStart));

                        if (typeof parsed?.detail === "string") {
                            message = parsed.detail;
                        } else if (Array.isArray(parsed?.detail) && parsed.detail.length > 0) {
                            message =
                                parsed.detail[0]?.msg ||
                                parsed.detail[0]?.message ||
                                message;
                        } else if (parsed?.message) {
                            message = parsed.message;
                        } else {
                            message = raw;
                        }
                    } else {
                        message = raw;
                    }

                    message = message.replace(/^\d{3}:\s*/, "");
                } catch {
                    message = String(detail).replace(/^\d{3}:\s*/, "");
                }
            }

            setSubmitError(message);
        } finally {
            setSubmitting(false);
        }
    };

    /* =========================
       FETCH ERROR STATE
    ========================== */
    if (!formLoading && fetchError) {
        const status =
            fetchError?.status ||
            fetchError?.response?.status ||
            fetchError?.response?.data?.status ||
            500;

        return (
            <div className="de-modal__backdrop">
                <div className="ms-auto de-modal d-flex flex-column justify-content-center align-items-center p-4">
                    <div
                        style={{
                            backgroundColor: "#fff8d5",
                            border: "1px solid #ffe7a3",
                            borderRadius: "4px",
                            padding: "14px 24px",
                            maxWidth: "500px",
                            textAlign: "center",
                            fontSize: "0.95rem",
                            color: "black",
                        }}
                    >
                        {status === 404
                            ? "Form configuration not found (404)."
                            : status === 403
                                ? "You don’t have permission to create users."
                                : status === 500
                                    ? "Server error (500). Please try again."
                                    : "Unable to load form right now."}
                    </div>

                    <button
                        className="de-modal__submit mt-3"
                        onClick={() => setCreate(false)}
                    >
                        Close
                    </button>
                </div>
            </div>
        );
    }

    const passwordWidgetIsPassword =
        uiSchema?.Password?.["ui:widget"] === "password";


    const getFieldTitle = (propertyKey, fallback) => {
        const field = schema?.properties?.[propertyKey];
        if (typeof field?.title === "string" && field.title.length) return field.title;
        return fallback;
    };

    const FieldLabel = ({ label, error }) => (
        <div
            className={`${error ? "error" : ""} d-block fw-bold mt-3`}
            style={{ color: error && "red"  }}
        >
            {label}
        </div>
    );

    /* =========================
       RENDER
    ========================== */
    return (
        <>
            <div className="de-modal__backdrop">
                <div
                    className="ms-auto h-100 w-100 d-flex justify-content-end"
                // onClick={(e) => {
                //     e.preventDefault();
                //     setCreate(false);
                // }}
                >
                    <div
                        className="pb-3 de-modal create-tenant-page d-flex flex-column evaluate-model-ui overflow-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* HEADER */}
                        <div
                            className="d-flex align-items-start justify-content-between mb-2 p-3"
                            style={{ borderBottom: "1px solid var(--glass-color)" }}
                        >
                            <div className="d-flex gap-2 align-tems-start w-100">
                                <div className="position-relative">
                                    <UserPlusIcon className="wf-icon-blue text-white bg-primary fw-bold p-2" />
                                    <PlusCircleIcon
                                        className="wf-icon-blue p-0"
                                        style={{
                                            width: "16px",
                                            height: "16px",
                                            position: "absolute",
                                            borderRadius: "50%",
                                            top: "7px",
                                            right: "5px",
                                        }}
                                    />
                                </div>

                                <div className="em-header flex-fill">
                                    <div className="d-flex justify-content-between w-100">
                                        <h2 className="main-header mb-0">Add User</h2>
                                        <button
                                            className="plain-button2 p-0"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setCreate(false);
                                            }}
                                        >
                                            <CloseIcon className="wf-icon text-secondary" />
                                        </button>
                                    </div>
                                    <p className="sub-title mb-0">
                                        Create a new user and assign the role
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Turn off autofill so browser doesn’t prefill old creds */}
                        <form className="flex-fill px-4" autoComplete="off">
                            {formLoading && <div>Loading form…</div>}

                            {!formLoading && schema && (
                                <>

                                    {/* NAME */}
                                    <div className="form-group">
                                        <FieldLabel label={getFieldTitle("Name", "Name")} error={errors.name} />
                                        <input
                                            type="text"
                                            autoComplete="off"
                                            placeholder="Enter Name"
                                            value={form.name || ""}
                                            onChange={(e) =>
                                                handleChange("name", e.target.value, true)
                                            }
                                        />
                                    </div>

                                    {/* ROLE (DROPDOWN) */}
                                    <div className="form-group">
                                        <FieldLabel label={getFieldTitle("Role", "Role")} error={errors.role} />
                                        <div className="de-modal__select">
                                            <select
                                                value={form.role || ""}
                                                onChange={(e) =>
                                                    handleChange("role", e.target.value, true)
                                                }
                                            >
                                                {schema.properties.Role.enum.map((val, idx) => (
                                                    <option key={val} value={val}>
                                                        {schema.properties.Role.enumNames?.[idx] || val}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* EMAIL */}
                                    <div className="form-group">
                                        <FieldLabel label={getFieldTitle("Email", "Email")} error={errors.email} />
                                        <input
                                            type="email"
                                            autoComplete="off"
                                            placeholder="Enter Email"
                                            value={form.email || ""}
                                            onChange={(e) =>
                                                handleChange("email", e.target.value, true)
                                            }
                                        />
                                        {errors.email && (
                                            <small className="text-secondary">
                                                Please enter a valid email address
                                            </small>
                                        )}
                                    </div>

                                    {/* PASSWORD */}
                                    <div className="form-group">
                                        <FieldLabel label={getFieldTitle("Password", "Password")} error={errors.password} />

                                        <div style={{ position: "relative" }}>
                                            <input
                                                type={
                                                    passwordWidgetIsPassword
                                                        ? showPassword
                                                            ? "text"
                                                            : "password"
                                                        : "text"
                                                }
                                                autoComplete="new-password"
                                                placeholder="Enter Password"
                                                value={form.password || ""}
                                                onChange={(e) =>
                                                    handleChange("password", e.target.value, true)
                                                }
                                            />

                                            {/* View/Hide */}
                                            {passwordWidgetIsPassword && (
                                                <span
                                                    style={{
                                                        position: "absolute",
                                                        right: "12px",
                                                        top: "50%",
                                                        transform: "translateY(-50%)",
                                                        cursor: "pointer",
                                                        fontSize: "0.85rem",
                                                        color: "#6c757d",
                                                        userSelect: "none",
                                                    }}
                                                    onClick={() => setShowPassword((p) => !p)}
                                                    title={showPassword ? "Hide password" : "Show password"}
                                                >
                                                    {showPassword ? "Hide" : "Show"}
                                                </span>
                                            )}
                                        </div>

                                        {/* Generate password */}
                                        <div className="d-flex justify-content-end mt-2">
                                            <button
                                                type="button"
                                                className="plain-button2 p-0"
                                                onClick={handleGeneratePassword}
                                                style={{ fontSize: "0.9rem" }}
                                            >
                                                Generate secure password
                                            </button>
                                        </div>

                                        {/* Strength */}
                                        {passwordValue && (
                                            <div style={{ marginTop: "10px" }}>
                                                <div
                                                    style={{
                                                        fontSize: "0.85rem",
                                                        color: "#6c757d",
                                                        marginBottom: "6px",
                                                    }}
                                                >
                                                    Strength:{" "}
                                                    <span className="fw-bold">
                                                        {passwordStrengthLabel}
                                                    </span>
                                                </div>

                                                {/* simple progress bar (no new classes required) */}
                                                <div
                                                    style={{
                                                        height: "6px",
                                                        borderRadius: "6px",
                                                        background: "rgba(0,0,0,0.08)",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            height: "100%",
                                                            width: `${Math.min(
                                                                100,
                                                                Math.round((passwordScore / 6) * 100)
                                                            )}%`,
                                                            background:
                                                                passwordStrengthLabel === "Strong"
                                                                    ? "#28a745"
                                                                    : passwordStrengthLabel === "Medium"
                                                                        ? "#ffc107"
                                                                        : "#dc3545",
                                                            transition: "width 150ms ease",
                                                        }}
                                                    />
                                                </div>

                                                <ul
                                                    style={{
                                                        marginTop: "10px",
                                                        marginBottom: "0px",
                                                        paddingLeft: "18px",
                                                        fontSize: "0.85rem",
                                                        color: "#6c757d",
                                                    }}
                                                >
                                                    <li style={{ color: passwordChecks.length ? "#28a745" : "#6c757d" }}>
                                                        At least 8 characters
                                                    </li>
                                                    <li style={{ color: passwordChecks.upper ? "#28a745" : "#6c757d" }}>
                                                        Includes an uppercase letter
                                                    </li>
                                                    <li style={{ color: passwordChecks.lower ? "#28a745" : "#6c757d" }}>
                                                        Includes a lowercase letter
                                                    </li>
                                                    <li style={{ color: passwordChecks.number ? "#28a745" : "#6c757d" }}>
                                                        Includes a number
                                                    </li>
                                                    <li style={{ color: passwordChecks.symbol ? "#28a745" : "#6c757d" }}>
                                                        Includes a symbol
                                                    </li>
                                                    <li style={{ color: passwordChecks.noSpaces ? "#28a745" : "#6c757d" }}>
                                                        No spaces
                                                    </li>
                                                </ul>

                                                {errors.password && (
                                                    <small className="text-secondary">
                                                        Password is too weak. Use 12+ chars with mixed types.
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* CONFIRM PASSWORD */}
                                    <div className="form-group">
                                        <FieldLabel label={getFieldTitle("Confirm Password", "Confirm Password")} error={errors.confirm_password} />

                                        <div style={{ position: "relative" }}>
                                            <input
                                                type={showConfirmPassword ? "text" : "password"}
                                                autoComplete="new-password"
                                                placeholder="Re-enter Password"
                                                value={form.confirm_password || ""}
                                                onChange={(e) =>
                                                    handleChange("confirm_password", e.target.value, true)
                                                }
                                            />

                                            <span
                                                style={{
                                                    position: "absolute",
                                                    right: "12px",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    cursor: "pointer",
                                                    fontSize: "0.85rem",
                                                    color: "#6c757d",
                                                    userSelect: "none",
                                                }}
                                                onClick={() => setShowConfirmPassword((p) => !p)}
                                                title={
                                                    showConfirmPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                            >
                                                {showConfirmPassword ? "Hide" : "Show"}
                                            </span>
                                        </div>

                                        {confirmPasswordValue && (
                                            <div style={{ marginTop: "10px" }}>
                                                <div
                                                    style={{
                                                        height: "6px",
                                                        borderRadius: "6px",
                                                        background: "rgba(0,0,0,0.08)",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            height: "100%",
                                                            width: `${confirmPasswordProgress}%`,
                                                            background: passwordsMatch ? "#28a745" : "#ffc107",
                                                            transition: "width 150ms ease",
                                                        }}
                                                    />
                                                </div>

                                                <div
                                                    style={{
                                                        fontSize: "0.85rem",
                                                        marginTop: "6px",
                                                        color: passwordsMatch ? "#28a745" : "#6c757d",
                                                    }}
                                                >
                                                    {passwordsMatch
                                                        ? "Passwords match"
                                                        : `Match progress: ${confirmPasswordProgress}%`}
                                                </div>
                                            </div>
                                        )}

                                        {errors.confirm_password && !confirmPasswordValue && (
                                            <small className="text-secondary">
                                                Please confirm the password
                                            </small>
                                        )}
                                    </div>
                                </>
                            )}
                        </form>

                        {submitError && (
                            <div
                                style={{
                                    backgroundColor: "#fff8d5",
                                    border: "1px solid #ffe7a3",
                                    borderRadius: "4px",
                                    padding: "10px 16px",
                                    fontSize: "0.9rem",
                                    color: "black",
                                    marginBottom: "12px",
                                }}
                            >
                                {submitError?.response?.data?.message ||
                                    "Failed to create user. Please try again."}
                            </div>
                        )}

                        <div className="px-3 d-a justify-content-between pt-3" style={{ borderTop: '0.5px solid var(--glass-color)' }}>
                            <span></span>
                            <Button
                                color="primary"
                                className=" m-0 "
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? "Creating..." : "Add User"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mobile-view-bottom"></div>
        </>
    );
}